import { Controller } from '@hotwired/stimulus'

// Shared across all instances intentionally — suffixed to the timestamp (see addAssociation)
// to prevent collisions.
let nestedRecordCounter = 0

export default class extends Controller {
  static targets = ['addLink', 'removeLink', 'template', 'counter', 'fieldsWrapper']
  static values = {
    placeholder: { type: String, default: 'NEW_RECORD' },
    max: { type: Number, default: 0 }
  }

  connect() {
    this.#refreshAddLink()
  }

  // Add a new nested field
  addAssociation(event) {
    event.preventDefault()
    if (this.maxValue > 0 && this.#activeCount >= this.maxValue) return
    const content = this.templateTarget.innerHTML.replace(
      new RegExp(this.placeholderValue, 'g'),
      `${new Date().getTime()}${++nestedRecordCounter}`
    )
    this.addLinkTarget.insertAdjacentHTML('beforebegin', content)

    const newFieldset = this.addLinkTarget.previousElementSibling
    const previousFieldset = this.fieldsWrapperTargets
      .filter(w => !this.isDestroyed(w) && w !== newFieldset)
      .pop()

    // Emit event for integration with other controllers
    window.dispatchEvent(new CustomEvent('nestedElementAdded', {
      detail: { fieldset: newFieldset, previousFieldset: previousFieldset }
    }))

    this.updatePositions()
    this.refreshCounters()
    this.#refreshAddLink()
  }

  // Remove a nested field
  removeAssociation(event) {
    event.preventDefault()
    const wrapper = event.target.closest('[data-nested-form-target="fieldsWrapper"]')

    if (wrapper.dataset.newRecord === 'true') {
      // New record: remove from DOM
      wrapper.remove()
    } else {
      // Existing record: mark for destruction
      wrapper.querySelector('input[name*=\'_destroy\']').value = 1
      wrapper.style.display = 'none'
      wrapper.dataset.destroyed = 'true'
    }

    window.dispatchEvent(new CustomEvent('nestedElementRemoved'))
    this.updatePositions()
    this.refreshCounters()
    this.#refreshAddLink()
  }

  // Move element up
  moveUp(event) {
    event.preventDefault()
    const wrapper = event.target.closest('[data-nested-form-target="fieldsWrapper"]')
    const visibleWrappers = this.fieldsWrapperTargets.filter(w => !this.isDestroyed(w))
    const index = visibleWrappers.indexOf(wrapper)

    if (index > 0) {
      visibleWrappers[index - 1].insertAdjacentElement('beforebegin', wrapper)
      this.updatePositions()
    }
  }

  // Move element down
  moveDown(event) {
    event.preventDefault()
    const wrapper = event.target.closest('[data-nested-form-target="fieldsWrapper"]')
    const visibleWrappers = this.fieldsWrapperTargets.filter(w => !this.isDestroyed(w))
    const index = visibleWrappers.indexOf(wrapper)

    if (index < visibleWrappers.length - 1) {
      visibleWrappers[index + 1].insertAdjacentElement('afterend', wrapper)
      this.updatePositions()
    }
  }

  // Update [position] fields for each wrapper
  updatePositions() {
    this.fieldsWrapperTargets
      .filter(wrapper => !this.isDestroyed(wrapper))
      .forEach((wrapper, index) => {
        const positionInput = wrapper.querySelector('input[name*=\'[position]\']')
        if (positionInput) positionInput.value = index
      })
    this.updateMoveButtonsVisibility()
  }

  // Hide useless move buttons (up on first, down on last)
  updateMoveButtonsVisibility() {
    const visibleWrappers = this.fieldsWrapperTargets.filter(w => !this.isDestroyed(w))
    visibleWrappers.forEach((wrapper, index) => {
      const moveUpBtn = wrapper.querySelector('.move-up-button')
      const moveDownBtn = wrapper.querySelector('.move-down-button')
      if (moveUpBtn) moveUpBtn.style.display = index === 0 ? 'none' : ''
      if (moveDownBtn) moveDownBtn.style.display = index === visibleWrappers.length - 1 ? 'none' : ''
    })
  }

  // Update counters (e.g., "Item 1", "Item 2")
  refreshCounters() {
    const activeCounters = this.counterTargets.filter(counter => {
      const wrapper = counter.closest('[data-nested-form-target="fieldsWrapper"]')
      return !this.isDestroyed(wrapper)
    })
    activeCounters.forEach((counter, index) => {
      counter.textContent = `${counter.dataset.text || ''} ${index + 1}`
    })
  }

  isDestroyed(wrapper) {
    if (!wrapper) return true
    const destroyInput = wrapper.querySelector('input[name*=\'_destroy\']')
    return (destroyInput && destroyInput.value === '1') || wrapper.dataset.destroyed === 'true'
  }

  get #activeCount() {
    return this.fieldsWrapperTargets.filter(w => !this.isDestroyed(w)).length
  }

  #refreshAddLink() {
    if (!this.hasAddLinkTarget) return
    if (this.maxValue > 0) {
      this.addLinkTarget.hidden = this.#activeCount >= this.maxValue
    }
  }
}
